import { useQuery } from '@apollo/client'
import { Group, Radio, Stack, Table, Title } from '@mantine/core'
import { Breadcrumbs } from 'components/Breadcrumbs'
import { CardTable } from 'components/CardTable'
import { FullPageError } from 'components/FullPageComponents'
import { FullContentLoader } from 'components/Loading'
import { MessageBox } from 'components/MessageBox'
import { SynCButton } from 'components/SyncButton'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  DiscussApplicantsTable,
  FreeForAllApplicantsTable,
} from '../components/DiscussionDashboard'
import { InternalGroupDiscussionDataOrderingKeyValue } from '../consts'
import { enumHandler } from 'util/parsing'
import { INTERNAL_GROUP_DISCUSSION_DATA } from '../queries'
import { InternalGroupDiscussionDataReturns } from '../types.graphql'
import { createStyles } from '@mantine/emotion'

interface InternalGroupDiscussionParams {
  internalGroupId: string
}

export const InternalGroupDiscussion: React.FC = () => {
  const navigate = useNavigate()
  const { classes } = useStyles()

  const [orderingKey, setOrderingKey] =
    useState<InternalGroupDiscussionDataOrderingKeyValue>(
      InternalGroupDiscussionDataOrderingKeyValue.PRIORITY
    )
  const { internalGroupId } = useParams<
    keyof InternalGroupDiscussionParams
  >() as InternalGroupDiscussionParams

  const { error, loading, data, refetch } =
    useQuery<InternalGroupDiscussionDataReturns>(
      INTERNAL_GROUP_DISCUSSION_DATA,
      {
        variables: { internalGroupId: internalGroupId, orderingKey },
        pollInterval: 20_000,
        fetchPolicy: 'network-only',
      }
    )

  function handleRedirect(module: 'applicant' | 'user', id: string) {
    if (module === 'applicant') {
      navigate(`/admissions/applicants/${id}`)
    } else if (module === 'user') {
      navigate(`/users/${id}`)
    }
  }

  if (error) return <FullPageError />

  if (loading || !data) return <FullContentLoader />

  const {
    internalGroupDiscussionData: {
      applicants,
      internalGroup,
      applicantsOpenForOtherPositions,
      applicantRecommendations,
    },
  } = data

  const breadcrumbsItems = [
    { label: 'Hjem', path: '/dashboard' },
    { label: 'Orvik', path: '/admissions' },
    { label: `Diskusjon ${internalGroup.name}`, path: '' },
  ]

  return (
    <Stack>
      <Breadcrumbs items={breadcrumbsItems} />
      <Group justify="space-between">
        <Title>Fordelingsmøte {internalGroup.name}</Title>
        <SynCButton
          refetchCallback={() => refetch()}
          refetchLoading={loading}
        />
      </Group>
      <Title order={2}>Kandidater tilgjengelige for vurdering</Title>
      <MessageBox type="info">
        Søkere blir fortløpende oppdatert i denne tabellen. Her har du mulighet
        til å se hvordan de andre gjengene, og deres egen gjeng prioriterer
        kandidaten. På høyre side av tabellen finnes en meny som markerer hva
        deres gjeng vil gjøre med kandidaten. Bare søkere som er markert med{' '}
        <b>Vil ha</b> eller <b>Reserve</b> kommer videre med i systemet. Alle
        kandidater må bli vurdert før fordelingsmøtet kan stenges.
      </MessageBox>
      <MessageBox type="warning">
        <strong>
          Obs! Du markerer ønsker på vegne av {internalGroup.name}
        </strong>
      </MessageBox>
      <Radio.Group
        label="Sorteringsmodus"
        value={orderingKey}
        onChange={enumHandler(
          InternalGroupDiscussionDataOrderingKeyValue,
          setOrderingKey
        )}
      >
        <Group>
          <Radio
            label="Prioriteringer"
            value={InternalGroupDiscussionDataOrderingKeyValue.PRIORITY}
          />
          <Radio
            label="Intervjutidspunkt"
            value={InternalGroupDiscussionDataOrderingKeyValue.INTERVIEW}
          />
        </Group>
      </Radio.Group>
      <DiscussApplicantsTable
        internalGroup={internalGroup}
        applicants={applicants}
      />

      <Title order={2}>Kandidater åpne for andre verv</Title>
      <FreeForAllApplicantsTable
        applicants={applicantsOpenForOtherPositions}
        internalGroupId={internalGroupId}
      />
      <Title order={2}>Anbefalte kandidater</Title>
      <CardTable>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Kandidat</Table.Th>
            <Table.Th>Begrunnelse</Table.Th>
            <Table.Th>Anbefalt av</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {applicantRecommendations.map((rec, i) => (
            <Table.Tr key={i}>
              <Table.Td
                className={classes.clickableTd}
                onClick={() => handleRedirect('applicant', rec.applicant.id)}
              >
                {rec.applicant.fullName}
              </Table.Td>
              <Table.Td>{rec.reasoning}</Table.Td>
              <Table.Td
                className={classes.clickableTd}
                onClick={() => handleRedirect('user', rec.recommendedBy.id)}
              >
                {rec.recommendedBy.fullName}
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </CardTable>
    </Stack>
  )
}

const useStyles = createStyles({
  clickableTd: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: 'var(--mantine-color-gray-0)',
      textDecoration: 'underline',
    },
  },
})
